import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthServiceService } from '../../shareModule/services/auth-service.service';
import { CommonService } from '../../shareModule/services/common.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { SharedService } from '../../shareModule/services/shared.service';

@Component({
  selector: 'app-edit-booking',
  templateUrl: './edit-booking.component.html',
  styleUrls: ['./edit-booking.component.scss'],
})
export class EditBookingComponent implements OnInit, OnDestroy {
  @Input() selectedMapCategory: any = null;
  @Input() isSelectSubCat: boolean = false;
  @Input() currentSelectedTypes: any[] = [];
  @Input() filter: string = '';
  
  @Output() bookingSaved = new EventEmitter<{
    questions: any[];
    tags: string[];
    taggedUsers: any[];
    imageInfo: any;
  }>();

  @ViewChild('bookingImageFileSelector')
  bookingImageFileSelectorElement: ElementRef;

  public imgUrl = environment.ImageUrl;

  // Booking questions state
  public currentCategoryBookingQuestions: any[] = [];

  // Tags state
  public currentCategoryTagList: string[] = [];
  public newTagText = '';

  // Tagged users state
  public taggedUsers: string[] = [];
  public newTaggedUserText = '';
  public thankYouText = '';

  // Image state
  public currentCategoryBookingImageInfo: any = {};
  public ckEditorText: string = '';
  public contactWhatsApp = '';

  // Steps state
  public bookingSteps: Array<{ title: string; description: string }> = [];

  public tabs = [
    { title: 'Questions' },
    { title: 'Map Tags' },
    { title: 'Users Tags' },
    { title: 'Others' },
  ///  { title: 'Variables' },
    { title: 'Steps' }
  ];

  // Tab navigation state
  public currentTabIndex: number = 0;
  private scrollAmount: number = 200; // pixels to scroll per click
  private scrollHandler: (() => void) | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private auth: AuthServiceService,
    private commonService: CommonService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private sharedService: SharedService,
  ) { }

  ngOnInit(): void {
    // console.log(this.currentSelectedTypes, "currentSelectedTypescurrentSelctedTypescurrentSelectedTypescurrentSelectedTypes")
    this.initializeBookingData();

    // Set up scroll listener to update button states
    if (this.sharedService.isBrowser()) {
      const tabsHeader = this.getTabsHeaderElement();
      if (tabsHeader) {
        this.scrollHandler = () => {
          this.updateScrollButtonStates();
        };
        tabsHeader.addEventListener('scroll', this.scrollHandler);
        // Initial check
        this.updateScrollButtonStates();
      }
    }
  }

  ngOnDestroy(): void {
    // Clean up scroll event listener
    if (this.scrollHandler) {
      const tabsHeader = this.getTabsHeaderElement();
      if (tabsHeader) {
        tabsHeader.removeEventListener('scroll', this.scrollHandler);
      }
    }
  }

  private initializeBookingData(): void {
    const initData = this.currentSelectedTypes?.length
      ? this.currentSelectedTypes[0]
      : this.selectedMapCategory;
    // Initialize booking questions
    if (initData) {
      try {
        this.currentCategoryBookingQuestions = JSON.parse(
          initData.mapQuestions ?? '[]',
        );
      } catch (e) {
        console.error('Error parsing booking questions:', e);
        this.currentCategoryBookingQuestions = [];
      }
    }

    // Initialize tags
    this.currentCategoryTagList = initData?.bookingMoreInfos?.tagChoices ?? [];

    // Initialize tagged users
    this.taggedUsers = initData?.bookingMoreInfos?.tagUserChoices ?? [];

    // Initialize image info
    this.currentCategoryBookingImageInfo = (
      initData?.bookingMoreInfos?.uploadedImages as any[]
    )?.at(0);
    this.thankYouText = initData?.bookingMoreInfos?.thankYouText;
    this.contactWhatsApp = initData?.bookingMoreInfos?.contactWhatsApp;

    if (!this.currentCategoryBookingImageInfo) {
      this.currentCategoryBookingImageInfo = {
        url: '',
        imageFile: null,
        caption: '',
        convertedFileUrl: null,
      };
    }

    // Initialize editor text with caption
    this.ckEditorText = this.currentCategoryBookingImageInfo.caption || '';

    // Initialize booking steps
    this.initializeBookingSteps(initData);
  }

  private initializeBookingSteps(initData: any): void {
    // Default steps
    const defaultSteps = [
      {
        title: 'Enquire',
        description: 'Start your Order using Kachiing if you dont wish to decide'
      },
      {
        title: 'Fast Match',
        description: 'Kachiing helps Match you to the nearest and best/popular vendor'
      },
      {
        title: 'Get Whatsapp',
        description: 'Results WhatsApped to you. Awaiting your decision'
      }
    ];

    // Try to load existing steps from bookingMoreInfos
    if (initData?.bookingMoreInfos?.steps && Array.isArray(initData.bookingMoreInfos.steps)) {
      // If steps exist and have 3 items, use them; otherwise use defaults
      if (initData.bookingMoreInfos.steps.length === 3) {
        // Create new objects to avoid reference issues
        this.bookingSteps = initData.bookingMoreInfos.steps.map((step: any) => ({
          title: String(step.title || ''),
          description: String(step.description || '')
        }));
      } else {
        // Create new array with new objects
        this.bookingSteps = defaultSteps.map(step => ({ ...step }));
      }
    } else {
      // No existing steps, use defaults - create new objects
      this.bookingSteps = defaultSteps.map(step => ({ ...step }));
    }
  }

  // Question management methods
  addNewQuestionToMapBooking(): void {
    this.currentCategoryBookingQuestions.push({
      questionText: '',
      type: 'text',
      isTag: false,
      choices: [],
    });
  }

  removeBookingQuestion(index: number): void {
    this.currentCategoryBookingQuestions.splice(index, 1);
  }

  addChoiceToBookingQuestion(qIndex: number): void {
    const question = this.currentCategoryBookingQuestions[qIndex];
    if (question.type === 'multipleChoice') {
      if (!question.choices) {
        question.choices = [];
      }
      question.choices.push('');
    }
  }

  removeChoiceFromBookingQuestion(qIndex: number, cIndex: number): void {
    const question = this.currentCategoryBookingQuestions[qIndex];
    if (question.choices && question.choices.length > cIndex) {
      question.choices.splice(cIndex, 1);
    }
  }

  trackByChoice(index: number, choice: string): any {
    return index;
  }

  // Tag management methods
  addNewTag(): void {
    if (this.newTagText?.trim()) {
      this.currentCategoryTagList.push(this.newTagText.trim());
      this.newTagText = '';
    }
  }

  removeBookingTagOptionFromCategory(index: number): void {
    this.currentCategoryTagList.splice(index, 1);
  }

  // User management methods
  addNewTaggedUser(): void {
    if (this.newTaggedUserText?.trim()) {
      this.taggedUsers.push(this.newTaggedUserText.trim());
      this.newTaggedUserText = '';
    }
  }

  removeTaggedUser(index: number): void {
    this.taggedUsers.splice(index, 1);
  }

  // Steps management methods
  updateStepTitle(index: number, event: any): void {
    const value = event.target.value;
    if (this.bookingSteps && this.bookingSteps[index] !== undefined) {
      // Update the step object
      this.bookingSteps[index] = {
        ...this.bookingSteps[index],
        title: value
      };
      console.log('Updated step title:', index, value, this.bookingSteps[index]);
      console.log('Full bookingSteps array:', JSON.parse(JSON.stringify(this.bookingSteps)));
      this.cdr.detectChanges();
    }
  }

  updateStepDescription(index: number, event: any): void {
    const value = event.target.value;
    if (this.bookingSteps && this.bookingSteps[index] !== undefined) {
      // Update the step object
      this.bookingSteps[index] = {
        ...this.bookingSteps[index],
        description: value
      };
      this.cdr.detectChanges();
    }
  }

  trackByStep(index: number, step: { title: string; description: string }): any {
    return index;
  }

  // Image management methods
  async setBookingImage(event: any): Promise<void> {
    try {
      [
        this.currentCategoryBookingImageInfo.convertedFileUrl,
        this.currentCategoryBookingImageInfo.imageFile,
      ] = await this.commonService.convertImageToURL(event);
    } catch (error) {
      console.error('Error setting booking image:', error);
      this.toastr.error('Error processing image');
    }
  }

  openSelectBookingImage(): void {
    this.bookingImageFileSelectorElement?.nativeElement?.click();
  }

  // Save and close methods
  saveBookingQuestions(): void {
    console.log(
      'Saving booking questions:',
      this.currentCategoryBookingQuestions,
    );

    let selectedCategoryId = this.selectedMapCategory?.id;
    if (this.isSelectSubCat) {
      selectedCategoryId = this.selectedMapCategory?.categorieChildId;
    }

    this.saveTags(selectedCategoryId);
  }

  private saveTags(selectedCategoryId: number): void {
    // Save tags and tagged users
    this.auth
      .setTagsOfCategoryOnMap(
        this.currentSelectedTypes?.length
          ? this.currentSelectedTypes[0]?.id
          : selectedCategoryId,
        this.currentCategoryTagList,
        this.taggedUsers,
        !!this.currentSelectedTypes?.length,
      )
      .subscribe({
        next: (data) => {
          this.toastr.success('Tags and tagged users saved successfully!');
          this.checkAllCompleted();
          this.saveQuestions(selectedCategoryId);
        },
        error: (error) => {
          this.toastr.error(
            'Error saving tags and tagged users: ' + error.error.message,
          );
          this.checkAllCompleted();
        },
      });
  }

  private saveContactWhatsapp() {
    const { id, isType } = this.getTypeAndIdOfCurrentCategory();

    this.auth
      .changeCategoryBookingProperty(
        id,
        'contactWhatsApp',
        this.contactWhatsApp,
        isType,
      )
      .subscribe({
        next: (data) => {
          this.toastr.success('whatsapp phone saved');
          this.checkAllCompleted();
          this.saveBookingSteps();
        },
        error: (error) => {
          this.toastr.error(error.error.message);
        },
      });
  }

  private saveBookingSteps() {
    const { id, isType } = this.getTypeAndIdOfCurrentCategory();

    this.auth
      .changeCategoryBookingProperty(
        id,
        'steps',
        this.bookingSteps,
        isType,
      )
      .subscribe({
        next: (data) => {
          this.toastr.success('Steps saved successfully!');
          this.checkAllCompleted();
          // window.location.reload();
        },
        error: (error) => {
          this.toastr.error('Error saving steps: ' + error.error.message);
          // window.location.reload();
        },
      });
  }

  private getTypeAndIdOfCurrentCategory() {
    let selectedCategoryId = this.selectedMapCategory?.id;
    if (this.isSelectSubCat) {
      selectedCategoryId = this.selectedMapCategory?.categorieChildId;
    }

    const id = this.currentSelectedTypes?.length
      ? this.currentSelectedTypes[0]?.id
      : selectedCategoryId;
    const isType = !!this.currentSelectedTypes?.length;
    return { id, isType };
  }

  private saveQuestions(selectedCategoryId: number): void {
    // Save questions
    this.auth
      .setMapQuestions(
        this.currentSelectedTypes?.length
          ? this.currentSelectedTypes[0]?.id
          : selectedCategoryId,
        JSON.stringify(this.currentCategoryBookingQuestions),
        !!this.currentSelectedTypes?.length,
      )
      .subscribe({
        next: () => {
          this.toastr.success('Questions saved successfully!');
          this.checkAllCompleted();
          this.uploadBookingImage(selectedCategoryId);
        },
        error: (error) => {
          this.toastr.error('Error saving questions: ' + error.error.message);
          this.checkAllCompleted();
        },
      });
  }

  private changeThankYouText() {
    const { id, isType } = this.getTypeAndIdOfCurrentCategory();
    this.auth
      .changeCategoryBookingProperty(
        id,
        'thankYouText',
        this.thankYouText,
        isType,
      )
      .subscribe({
        next: () => {
          this.toastr.success('Thank you text changed');
          this.saveContactWhatsapp();
          this.checkAllCompleted();

        },
        error: (error) => {
          this.toastr.error(error.error.message);
        },
      });
  }

  private uploadBookingImage(selectedCategoryId: number): void {
    this.currentCategoryBookingImageInfo.caption = this.ckEditorText;

    this.auth
      .uploadBookingImage(
        this.currentSelectedTypes?.length
          ? this.currentSelectedTypes[0]?.id
          : selectedCategoryId,
        this.currentCategoryBookingImageInfo.imageFile,
        this.currentCategoryBookingImageInfo.caption,
        !!this.currentSelectedTypes?.length,
      )
      .subscribe({
        next: () => {
          this.toastr.success('Image saved successfully!');
          this.changeThankYouText();

          this.checkAllCompleted();
        },
        error: (error) => {
          console.log(error);
          this.toastr.error('Error saving image: ' + error.error.message);
          this.checkAllCompleted();
          this.changeThankYouText();

        },
      });
  }

  completedOperations = 0;
  totalOperations = 5; // tags, questions, and potentially image
  actualTotalOperations = 4;

  private checkAllCompleted = () => {
    this.completedOperations++;
    if (this.completedOperations >= this.actualTotalOperations) {
      // Update caption from editor text before emitting
      this.currentCategoryBookingImageInfo.caption = this.ckEditorText;

      // All operations completed, emit event with updated data and close modal
      this.bookingSaved.emit({
        questions: this.currentCategoryBookingQuestions,
        tags: this.currentCategoryTagList,
        taggedUsers: this.taggedUsers,
        imageInfo: this.currentCategoryBookingImageInfo,
      });
      this.closeModal();
    }
    this.cdr.detectChanges();
  };

  closeModal(): void {
    this.activeModal.close();
  }

  // Tab scroll navigation methods
  scrollTabsLeft(): void {
    const tabsHeader = this.getTabsHeaderElement();
    if (tabsHeader) {
      tabsHeader.scrollBy({
        left: -this.scrollAmount,
        behavior: 'smooth'
      });
      // Update button states after scroll
      setTimeout(() => this.updateScrollButtonStates(), 100);
    }
  }

  scrollTabsRight(): void {
    const tabsHeader = this.getTabsHeaderElement();
    if (tabsHeader) {
      tabsHeader.scrollBy({
        left: this.scrollAmount,
        behavior: 'smooth'
      });
      // Update button states after scroll
      setTimeout(() => this.updateScrollButtonStates(), 100);
    }
  }

  private getTabsHeaderElement(): HTMLElement | null {
    // Search for the tabs header element in the DOM
    // Using a more specific selector to find the header within this modal
    const modalElement = document.querySelector('.edit-booking-modal');
    if (modalElement) {
      const tabsHeader = modalElement.querySelector('.tabs__header');
      return tabsHeader as HTMLElement;
    }
    // Fallback: search globally
    const tabsContainer = document.querySelector('.tabs__header');
    return tabsContainer as HTMLElement;
  }

  canScrollLeft(): boolean {
    const tabsHeader = this.getTabsHeaderElement();
    if (!tabsHeader) return false;
    return tabsHeader.scrollLeft > 0;
  }

  canScrollRight(): boolean {
    const tabsHeader = this.getTabsHeaderElement();
    if (!tabsHeader) return false;
    const maxScroll = tabsHeader.scrollWidth - tabsHeader.clientWidth;
    return tabsHeader.scrollLeft < maxScroll - 1; // -1 to account for rounding
  }

  private updateScrollButtonStates(): void {
    // Force change detection to update button states
    this.cdr.detectChanges();
  }

  onTabChange(index: number): void {
    this.currentTabIndex = index;
  }
}
